import React from 'react';
import { Eye, Pencil, Copy, Trash2, Check } from 'lucide-react';
import { Modal, ConfirmModal } from '../ui/Modal';
import ContextMenu from '../ui/ContextMenu';
import OsScroll from '../ui/OsScroll';
import CalendarToolbar from './CalendarToolbar';
import MonthView from './MonthView';
import WeekView from './WeekView';
import DayView from './DayView';
import AgendaView from './AgendaView';
import EventPopover from './EventPopover';
import EventEditorModal from './EventEditorModal';
import RecurrenceScopeDialog from './RecurrenceScopeDialog';
import { isReadOnlyEvent } from './calendarViewHelpers';

/** The calendar page markup: toolbar, the active view, popovers and dialogs. Plain function called during render, so the element tree is unchanged. */
export function renderCalendarView({
  t,
  teams,
  view,
  popover,
  focusedDate,
  calendarFilter,
  setView,
  setFocusedDate,
  setCalendarFilter,
  timeFormat,
  events,
  visibleEvents,
  setPopover,
  editorDraft,
  setEditorDraft,
  deleteTarget,
  setDeleteTarget,
  importReport,
  setImportReport,
  eventMenu,
  setEventMenu,
  scopeDialog,
  setScopeDialog,
  navigate,
  handleSelectEvent,
  handleEventContextMenu,
  handleUpdateEventTimesGuarded,
  writeOverride,
  handleDuplicateEvent,
  applyEditToMaster,
  deleteThisOccurrence,
  handleSaveDraft,
  requestDelete,
  confirmDelete,
  handleInviteResponse,
  handleCreateDraftFromGrid,
  handleImportIcs,
  handleExportIcs,
  handleQuickCreate,
}) {
  const viewProps = {
    focusedDate,
    events: visibleEvents,
    timeFormat,
    onSelectEvent: handleSelectEvent,
    onEventContextMenu: handleEventContextMenu,
  };

  return (
    <div className="cal-view" data-testid="calendar-view">
      <CalendarToolbar
        view={view}
        focusedDate={focusedDate}
        calendarFilter={calendarFilter}
        onToday={() => setFocusedDate(new Date())}
        onNavigate={navigate}
        onSetView={setView}
        onSetCalendarFilter={setCalendarFilter}
        onNewEvent={() => setEditorDraft({})}
        onImportIcs={handleImportIcs}
        onExportIcs={handleExportIcs}
      />

      {view === 'month' && (
        <MonthView
          {...viewProps}
          onSelectDay={(day) => setFocusedDate(day)}
          onOpenDay={(day) => {
            setFocusedDate(day);
            setView('day');
          }}
          onQuickCreate={handleQuickCreate}
        />
      )}
      {view === 'week' && (
        <WeekView
          {...viewProps}
          draftOpen={Boolean(editorDraft)}
          onCreateDraft={handleCreateDraftFromGrid}
          onUpdateEventTimes={handleUpdateEventTimesGuarded}
        />
      )}
      {view === 'day' && (
        <DayView
          {...viewProps}
          draftOpen={Boolean(editorDraft)}
          onCreateDraft={handleCreateDraftFromGrid}
          onUpdateEventTimes={handleUpdateEventTimesGuarded}
        />
      )}
      {view === 'agenda' && <AgendaView {...viewProps} />}

      {popover && (
        <EventPopover
          event={popover.event}
          anchorRect={popover.anchorRect}
          timeFormat={timeFormat}
          onClose={() => setPopover(null)}
          onEdit={(event) => {
            setPopover(null);
            setEditorDraft(event);
          }}
          onDelete={(event) => requestDelete(event)}
          onRespondInvite={handleInviteResponse}
          readOnly={isReadOnlyEvent(popover.event, teams)}
        />
      )}

      {editorDraft && (
        <EventEditorModal
          draft={editorDraft}
          onClose={() => setEditorDraft(null)}
          onSave={handleSaveDraft}
          onDelete={(event) => setDeleteTarget(event)}
        />
      )}

      {eventMenu && eventMenu.event.pending_invite && (
        <ContextMenu
          x={eventMenu.x}
          y={eventMenu.y}
          items={[
            {
              icon: <Check size={13} />,
              label: t('calendar.invite.acceptInvitation'),
              onClick: () => handleInviteResponse(eventMenu.event, 'accept'),
            },
            {
              icon: <Trash2 size={13} />,
              label: t('calendar.invite.declineInvitation'),
              onClick: () => handleInviteResponse(eventMenu.event, 'decline'),
            },
          ]}
          onClose={() => setEventMenu(null)}
        />
      )}

      {eventMenu && !eventMenu.event.pending_invite && (
        <ContextMenu
          x={eventMenu.x}
          y={eventMenu.y}
          items={[
            {
              icon: <Eye size={13} />,
              label: t('calendar.action.open'),
              onClick: () =>
                setPopover({
                  event: eventMenu.event,
                  // Anchor the popover where the menu was invoked.
                  anchorRect: { left: eventMenu.x, right: eventMenu.x, top: eventMenu.y, bottom: eventMenu.y },
                }),
            },
            // Viewers on a team calendar get Open ONLY — mirrors the server's
            // editor+ push rule (Phase 3); falsy entries are skipped by
            // ContextMenu, the WorkspaceManager conditional-items shape.
            ...(isReadOnlyEvent(eventMenu.event, teams)
              ? []
              : [
                  {
                    icon: <Pencil size={13} />,
                    label: t('common.edit'),
                    onClick: () => setEditorDraft(eventMenu.event),
                  },
                  {
                    icon: <Copy size={13} />,
                    label: t('calendar.action.duplicate'),
                    onClick: () => handleDuplicateEvent(eventMenu.event),
                  },
                  {
                    icon: <Trash2 size={13} />,
                    label: t('common.delete'),
                    onClick: () => requestDelete(eventMenu.event),
                  },
                ]),
          ]}
          onClose={() => setEventMenu(null)}
        />
      )}

      {scopeDialog && (
        <RecurrenceScopeDialog
          mode={scopeDialog.mode}
          eventTitle={scopeDialog.occurrence.title}
          onClose={() => setScopeDialog(null)}
          onThis={async () => {
            const { mode, occurrence, fields } = scopeDialog;
            setScopeDialog(null);
            if (mode === 'edit') await writeOverride(occurrence, fields);
            else await deleteThisOccurrence(occurrence);
          }}
          onAll={async () => {
            const { mode, occurrence, fields } = scopeDialog;
            setScopeDialog(null);
            if (mode === 'edit') {
              await applyEditToMaster(occurrence, fields);
              return;
            }
            // Delete-all: hand the MASTER to the existing ConfirmModal path
            // (its confirm sweeps the overrides too).
            const master =
              events.find(
                (e) => e.uid === (occurrence.override_of?.master_uid || occurrence.uid) && e.recurrence
              ) || occurrence;
            setDeleteTarget(master);
          }}
        />
      )}

      {importReport && (
        <Modal isOpen onClose={() => setImportReport(null)} title={t('calendar.toast.importComplete')} size="md">
          <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <p style={{ margin: 0 }}>
              {t('calendar.toast.importedN', { count: importReport.imported })}
            </p>
            {importReport.skipped.length > 0 && (
              <>
                <p style={{ margin: 0, fontWeight: 600 }}>
                  {t('calendar.toast.skippedN', { count: importReport.skipped.length })}
                </p>
                {/* OsScroll, not raw overflow (fix round 4 sweep — gotcha:
                    macOS overlay scrollbars hijack hand-rolled regions). */}
                <OsScroll style={{ maxHeight: 200 }}>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {importReport.skipped.map((s, i) => (
                      <li key={i}>
                        {s.summary} — {s.reason}
                      </li>
                    ))}
                  </ul>
                </OsScroll>
              </>
            )}
          </div>
        </Modal>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={t('calendar.deleteEventTitle')}
        message={t('calendar.deleteEventMessage', {
          title: deleteTarget?.title || t('calendar.noTitle'),
        })}
        confirmText={t('common.delete')}
        type="danger"
      />
    </div>
  );
}
